import { useEffect, useState } from "react";
import {
  Save, Loader2, KeyRound, FileText, MessageCircle, Building2, ReceiptText,
  Landmark, Wrench, Users, Plus, Trash2, Pencil, Shield, Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { deriveState } from "@/lib/gstStates";
import { cn } from "@/lib/utils";

const SECTIONS = [
  { id: "account", label: "Account", icon: KeyRound },
  { id: "users", label: "Users & Roles", icon: Users },
  { id: "company", label: "Company", icon: Building2 },
  { id: "invoice", label: "Invoice", icon: ReceiptText },
  { id: "bank", label: "Bank & UPI", icon: Landmark },
  { id: "quotation", label: "Quotation", icon: FileText },
  { id: "service", label: "Service Receipt", icon: Wrench },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "email", label: "Email (SMTP)", icon: Mail },
];

const Section = ({ children }) => (
  <section className="bg-white border border-[#E2E8F0] rounded-lg p-6 space-y-4">{children}</section>
);
const SaveBtn = ({ onClick, saving, label, testId }) => (
  <div className="flex justify-end">
    <Button onClick={onClick} disabled={saving} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2" data-testid={testId}>
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> {label}</>}
    </Button>
  </div>
);
const FieldLabel = ({ children }) => (
  <Label className="text-xs uppercase tracking-wide text-[#475569]">{children}</Label>
);

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("account");
  const [loading, setLoading] = useState(true);

  const [terms, setTerms] = useState("");
  const [store, setStore] = useState({ name: "", address: "", phone: "", email: "", gstin: "", state: "", state_code: "" });
  const [acct, setAcct] = useState({ current_password: "", new_email: "", new_password: "", confirm: "" });
  const [quoteCfg, setQuoteCfg] = useState({ start_seq: 1, terms: "" });
  const [wa, setWa] = useState("");
  const [invCfg, setInvCfg] = useState({ prefix: "INV/", suffix: "", fy: "", start_seq: 1, default_gst_rate: 18, declaration: "", signature_image: "" });
  const [bank, setBank] = useState({ show: true, ac_holder: "", bank_name: "", ac_no: "", branch: "", ifsc: "", upi_id: "" });
  const [smtp, setSmtp] = useState({ host: "", port: 587, username: "", password: "", from_name: "", from_email: "", use_tls: true, has_password: false });
  const [smtpLoaded, setSmtpLoaded] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);

  const [savingTerms, setSavingTerms] = useState(false);
  const [savingStore, setSavingStore] = useState(false);
  const [savingAcct, setSavingAcct] = useState(false);
  const [savingQuote, setSavingQuote] = useState(false);
  const [savingWa, setSavingWa] = useState(false);
  const [savingInv, setSavingInv] = useState(false);
  const [savingBank, setSavingBank] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, s, q, w, iv, bk] = await Promise.all([
          api.get("/settings/terms"), api.get("/settings/store"),
          api.get("/settings/quote"), api.get("/settings/whatsapp"),
          api.get("/settings/invoice"), api.get("/settings/bank"),
        ]);
        setTerms(t.data.content || "");
        setStore((prev) => ({ ...prev, ...(s.data || {}) }));
        setQuoteCfg({ start_seq: q.data.start_seq || 1, terms: q.data.terms || "" });
        setWa(w.data.content || "");
        setInvCfg({
          prefix: iv.data.prefix ?? "INV/", suffix: iv.data.suffix ?? "", fy: iv.data.fy ?? "",
          start_seq: iv.data.start_seq || 1, default_gst_rate: iv.data.default_gst_rate ?? 18,
          declaration: iv.data.declaration || "", signature_image: iv.data.signature_image || "",
        });
        setBank((prev) => ({ ...prev, ...(bk.data || {}) }));
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (tab !== "email" || smtpLoaded) return;
    (async () => {
      try { const { data } = await api.get("/settings/smtp"); setSmtp((p) => ({ ...p, ...data })); setSmtpLoaded(true); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
    // eslint-disable-next-line
  }, [tab]);

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      const { has_password, ...payload } = smtp;
      await api.put("/settings/smtp", payload);
      toast.success("SMTP settings saved");
      setSmtp((p) => ({ ...p, password: "", has_password: has_password || !!payload.password }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingSmtp(false); }
  };

  useEffect(() => {
    if (user?.email && !acct.new_email) setAcct((a) => ({ ...a, new_email: user.email }));
    // eslint-disable-next-line
  }, [user]);

  const onStoreGst = (val) => {
    const up = val.toUpperCase();
    const d = deriveState(up);
    setStore((s) => ({ ...s, gstin: up, state: d.state || s.state, state_code: d.code || s.state_code }));
  };

  const saveStore = async () => {
    setSavingStore(true);
    try { await api.put("/settings/store", store); toast.success("Company info saved"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSavingStore(false); }
  };
  const saveTerms = async () => {
    setSavingTerms(true);
    try { await api.put("/settings/terms", { content: terms }); toast.success("Terms saved"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSavingTerms(false); }
  };
  const saveQuote = async () => {
    setSavingQuote(true);
    try { await api.put("/settings/quote", { start_seq: Number(quoteCfg.start_seq) || 1, terms: quoteCfg.terms }); toast.success("Quotation settings saved"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSavingQuote(false); }
  };
  const saveWa = async () => {
    setSavingWa(true);
    try { await api.put("/settings/whatsapp", { content: wa }); toast.success("WhatsApp template saved"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSavingWa(false); }
  };
  const saveInv = async () => {
    setSavingInv(true);
    try {
      await api.put("/settings/invoice", {
        prefix: invCfg.prefix, suffix: invCfg.suffix, fy: invCfg.fy,
        start_seq: Number(invCfg.start_seq) || 1, default_gst_rate: Number(invCfg.default_gst_rate) || 18,
        declaration: invCfg.declaration, signature_image: invCfg.signature_image || "",
      });
      toast.success("Invoice settings saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingInv(false); }
  };
  const saveBank = async () => {
    setSavingBank(true);
    try { await api.put("/settings/bank", bank); toast.success("Bank & UPI details saved"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSavingBank(false); }
  };

  const saveAccount = async () => {
    if (!acct.current_password) { toast.error("Enter current password to confirm changes"); return; }
    const payload = { current_password: acct.current_password };
    if (acct.new_email && acct.new_email.toLowerCase() !== (user?.email || "").toLowerCase()) {
      payload.new_email = acct.new_email.toLowerCase().trim();
    }
    if (acct.new_password) {
      if (acct.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
      if (acct.new_password !== acct.confirm) { toast.error("Passwords do not match"); return; }
      payload.new_password = acct.new_password;
    }
    if (!payload.new_email && !payload.new_password) { toast.error("Nothing to update"); return; }
    setSavingAcct(true);
    try {
      const { data } = await api.put("/auth/credentials", payload);
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      toast.success("Credentials updated. Use the new email/password next time.");
      setAcct({ current_password: "", new_email: data.email || acct.new_email, new_password: "", confirm: "" });
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingAcct(false); }
  };

  if (loading) return <div className="text-sm text-[#64748B]">Loading…</div>;

  const previewNo = `${invCfg.prefix}${invCfg.fy || "25-26"}/${String(Number(invCfg.start_seq) || 1).padStart(3, "0")}${invCfg.suffix}`;

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">Configuration</div>
        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-[#475569] mt-1">Company, invoicing, banking, receipts &amp; account — organized by section.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        {/* Submenu */}
        <aside className="lg:sticky lg:top-20 self-start">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0" data-testid="settings-submenu">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setTab(s.id)}
                data-testid={`settings-tab-${s.id}`}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors",
                  tab === s.id ? "bg-[#0055FF] text-white font-medium" : "text-[#475569] hover:bg-slate-100"
                )}
              >
                <s.icon className="w-4 h-4" strokeWidth={1.75} /> {s.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Panels */}
        <div className="space-y-6 min-w-0">
          {tab === "account" && (
            <Section>
              <div className="flex items-center gap-3">
                <KeyRound className="w-5 h-5 text-[#0055FF]" />
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B]">Account</div>
                  <h2 className="text-xl font-display font-medium tracking-tight">Login email &amp; password</h2>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <FieldLabel>Login Email</FieldLabel>
                  <Input type="email" value={acct.new_email} onChange={(e) => setAcct({ ...acct, new_email: e.target.value })} data-testid="account-email" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Current Password (required) *</FieldLabel>
                  <Input type="password" value={acct.current_password} onChange={(e) => setAcct({ ...acct, current_password: e.target.value })} data-testid="account-current" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>New Password (optional)</FieldLabel>
                  <Input type="password" value={acct.new_password} onChange={(e) => setAcct({ ...acct, new_password: e.target.value })} data-testid="account-new" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Confirm New Password</FieldLabel>
                  <Input type="password" value={acct.confirm} onChange={(e) => setAcct({ ...acct, confirm: e.target.value })} data-testid="account-confirm" />
                </div>
              </div>
              <SaveBtn onClick={saveAccount} saving={savingAcct} label="Update credentials" testId="save-account" />
            </Section>
          )}

          {tab === "users" && <UsersPanel currentUserId={user?.id} />}

          {tab === "company" && (
            <Section>
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-[#0055FF]" />
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B]">Company / Branding</div>
                  <h2 className="text-xl font-display font-medium tracking-tight">Appears on every invoice &amp; receipt</h2>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <FieldLabel>Company Name</FieldLabel>
                  <Input value={store.name} onChange={(e) => setStore({ ...store, name: e.target.value })} data-testid="store-name" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Phone</FieldLabel>
                  <Input value={store.phone} onChange={(e) => setStore({ ...store, phone: e.target.value })} data-testid="store-phone" />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <FieldLabel>Address</FieldLabel>
                  <Textarea rows={3} value={store.address} onChange={(e) => setStore({ ...store, address: e.target.value })} data-testid="store-address" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Email</FieldLabel>
                  <Input type="email" value={store.email} onChange={(e) => setStore({ ...store, email: e.target.value })} data-testid="store-email" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Company GSTIN</FieldLabel>
                  <Input value={store.gstin} onChange={(e) => onStoreGst(e.target.value)} className="font-mono" placeholder="e.g. 29ABCDE1234F1Z5" maxLength={15} data-testid="store-gstin" />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <FieldLabel>State &amp; Code (auto from GSTIN)</FieldLabel>
                  <Input value={store.state ? `${store.state}${store.state_code ? ` (Code: ${store.state_code})` : ""}` : ""} readOnly className="bg-slate-50" placeholder="Enter GSTIN above to auto-fill" data-testid="store-state" />
                  <p className="text-[11px] text-[#64748B]">Used to decide CGST+SGST (same state) vs IGST (other state) on invoices.</p>
                </div>
              </div>
              <SaveBtn onClick={saveStore} saving={savingStore} label="Save company" testId="save-store" />
            </Section>
          )}

          {tab === "invoice" && (
            <Section>
              <div className="flex items-center gap-3">
                <ReceiptText className="w-5 h-5 text-[#0055FF]" />
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B]">Tax Invoice</div>
                  <h2 className="text-xl font-display font-medium tracking-tight">Numbering, GST rate &amp; declaration</h2>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-1.5">
                  <FieldLabel>Prefix</FieldLabel>
                  <Input value={invCfg.prefix} onChange={(e) => setInvCfg({ ...invCfg, prefix: e.target.value })} className="font-mono" placeholder="INV/" data-testid="invoice-prefix" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Financial Year</FieldLabel>
                  <Input value={invCfg.fy} onChange={(e) => setInvCfg({ ...invCfg, fy: e.target.value })} className="font-mono" placeholder="25-26" data-testid="invoice-fy" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Suffix</FieldLabel>
                  <Input value={invCfg.suffix} onChange={(e) => setInvCfg({ ...invCfg, suffix: e.target.value })} className="font-mono" placeholder="(optional)" data-testid="invoice-suffix" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Starting Number</FieldLabel>
                  <Input type="number" min="1" value={invCfg.start_seq} onChange={(e) => setInvCfg({ ...invCfg, start_seq: e.target.value })} className="font-mono" data-testid="invoice-start-seq" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div className="space-y-1.5">
                  <FieldLabel>Standard GST Rate (%)</FieldLabel>
                  <Input type="number" min="0" step="0.5" value={invCfg.default_gst_rate} onChange={(e) => setInvCfg({ ...invCfg, default_gst_rate: e.target.value })} className="font-mono" data-testid="invoice-default-gst" />
                </div>
                <p className="text-[13px] text-[#475569] pb-2">Next invoice preview: <span className="font-mono text-[#0055FF]">{previewNo}</span></p>
              </div>
              <div className="space-y-1.5">
                <FieldLabel>Declaration</FieldLabel>
                <Textarea rows={4} value={invCfg.declaration} onChange={(e) => setInvCfg({ ...invCfg, declaration: e.target.value })} className="text-sm leading-relaxed" data-testid="invoice-declaration" />
              </div>
              <div className="space-y-1.5">
                <FieldLabel>Authorised Signatory signature</FieldLabel>
                <p className="text-[11px] text-[#64748B] -mt-1">Upload a PNG/JPG (transparent PNG looks best). It appears above the "Authorised Signatory" line on every invoice.</p>
                <div className="flex items-center gap-4 mt-1">
                  {invCfg.signature_image ? (
                    <div className="border border-[#E2E8F0] rounded-md p-2 bg-white">
                      <img src={invCfg.signature_image} alt="Signature preview" className="h-14 object-contain" data-testid="signature-preview" />
                    </div>
                  ) : (
                    <div className="h-[72px] w-40 border border-dashed border-[#CBD5E1] rounded-md flex items-center justify-center text-[11px] text-[#94A3B8]">No signature</div>
                  )}
                  <div className="flex flex-col gap-2">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      data-testid="signature-upload"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 1024 * 1024) { toast.error("Please choose an image under 1 MB"); e.target.value = ""; return; }
                        const reader = new FileReader();
                        reader.onload = () => setInvCfg((c) => ({ ...c, signature_image: reader.result }));
                        reader.readAsDataURL(f);
                      }}
                      className="text-xs"
                    />
                    {invCfg.signature_image && (
                      <Button type="button" variant="outline" size="sm" onClick={() => setInvCfg({ ...invCfg, signature_image: "" })} className="h-8 text-xs w-fit" data-testid="signature-remove">
                        Remove signature
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <SaveBtn onClick={saveInv} saving={savingInv} label="Save invoice settings" testId="save-invoice-settings" />
            </Section>
          )}

          {tab === "bank" && (
            <Section>
              <div className="flex items-center gap-3">
                <Landmark className="w-5 h-5 text-[#0055FF]" />
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B]">Bank &amp; UPI</div>
                  <h2 className="text-xl font-display font-medium tracking-tight">Payment details on invoices</h2>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={!!bank.show} onCheckedChange={(v) => setBank({ ...bank, show: v })} data-testid="bank-show" />
                <Label className="text-sm">Show bank &amp; UPI details on the tax invoice</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <FieldLabel>A/c Holder's Name</FieldLabel>
                  <Input value={bank.ac_holder} onChange={(e) => setBank({ ...bank, ac_holder: e.target.value })} data-testid="bank-ac-holder" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Bank Name</FieldLabel>
                  <Input value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} data-testid="bank-name" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Account Number</FieldLabel>
                  <Input value={bank.ac_no} onChange={(e) => setBank({ ...bank, ac_no: e.target.value })} className="font-mono" data-testid="bank-ac-no" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Branch</FieldLabel>
                  <Input value={bank.branch} onChange={(e) => setBank({ ...bank, branch: e.target.value })} data-testid="bank-branch" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>IFSC Code</FieldLabel>
                  <Input value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase() })} className="font-mono" data-testid="bank-ifsc" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>UPI ID</FieldLabel>
                  <Input value={bank.upi_id} onChange={(e) => setBank({ ...bank, upi_id: e.target.value })} className="font-mono" placeholder="name@bank" data-testid="bank-upi" />
                </div>
              </div>
              <SaveBtn onClick={saveBank} saving={savingBank} label="Save bank & UPI" testId="save-bank" />
            </Section>
          )}

          {tab === "quotation" && (
            <Section>
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#0055FF]" />
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B]">Quotation</div>
                  <h2 className="text-xl font-display font-medium tracking-tight">Numbering &amp; terms</h2>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <FieldLabel>Starting Number</FieldLabel>
                  <Input type="number" min="1" value={quoteCfg.start_seq} onChange={(e) => setQuoteCfg({ ...quoteCfg, start_seq: e.target.value })} className="font-mono" data-testid="quote-start-seq" />
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel>Quotation Terms &amp; Conditions</FieldLabel>
                <Textarea rows={10} value={quoteCfg.terms} onChange={(e) => setQuoteCfg({ ...quoteCfg, terms: e.target.value })} className="font-mono text-sm leading-relaxed" data-testid="quote-terms-textarea" />
              </div>
              <SaveBtn onClick={saveQuote} saving={savingQuote} label="Save quotation settings" testId="save-quote-settings" />
            </Section>
          )}

          {tab === "service" && (
            <Section>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B]">Service Receipt</div>
                <h2 className="text-xl font-display font-medium tracking-tight">Terms &amp; Conditions</h2>
                <p className="text-sm text-[#475569]">Edit the terms printed on every service receipt.</p>
              </div>
              <Textarea rows={14} value={terms} onChange={(e) => setTerms(e.target.value)} className="font-mono text-sm leading-relaxed" data-testid="terms-textarea" />
              <SaveBtn onClick={saveTerms} saving={savingTerms} label="Save terms" testId="save-terms" />
            </Section>
          )}

          {tab === "whatsapp" && (
            <Section>
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-[#16A34A]" />
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B]">WhatsApp</div>
                  <h2 className="text-xl font-display font-medium tracking-tight">"Ready to collect" message</h2>
                  <p className="text-sm text-[#475569]">Copy this from the Services list (green icon) and paste into WhatsApp.</p>
                </div>
              </div>
              <Textarea rows={5} value={wa} onChange={(e) => setWa(e.target.value)} className="text-sm leading-relaxed" data-testid="whatsapp-template-textarea" />
              <p className="text-[11px] text-[#64748B]">
                Placeholders: <span className="font-mono">{"{customer_name}"}</span>, <span className="font-mono">{"{model}"}</span>, <span className="font-mono">{"{service_id}"}</span>, <span className="font-mono">{"{amount}"}</span>, <span className="font-mono">{"{store_name}"}</span>
              </p>
              <SaveBtn onClick={saveWa} saving={savingWa} label="Save template" testId="save-whatsapp-template" />
            </Section>
          )}

          {tab === "email" && (
            <Section>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#0055FF]" />
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B]">Email</div>
                  <h2 className="text-xl font-display font-medium tracking-tight">SMTP Profile</h2>
                  <p className="text-sm text-[#475569]">Configure your outgoing mail server to email invoices &amp; quotations to customers.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5 sm:col-span-2">
                  <FieldLabel>SMTP Host</FieldLabel>
                  <Input value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} placeholder="smtp.gmail.com" className="font-mono" data-testid="smtp-host" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Port</FieldLabel>
                  <Input type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: Number(e.target.value) })} placeholder="587" className="font-mono" data-testid="smtp-port" />
                </div>
                <div className="space-y-1.5 flex items-end">
                  <label className="flex items-center gap-2 text-sm text-[#475569] cursor-pointer h-10">
                    <input type="checkbox" checked={smtp.use_tls} onChange={(e) => setSmtp({ ...smtp, use_tls: e.target.checked })} className="w-4 h-4 accent-[#0055FF]" data-testid="smtp-tls" />
                    Use STARTTLS (uncheck for port 465 / SSL)
                  </label>
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Username</FieldLabel>
                  <Input value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} placeholder="you@gmail.com" className="font-mono" data-testid="smtp-username" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Password {smtp.has_password && <span className="text-[10px] text-[#16A34A]">(saved — leave blank to keep)</span>}</FieldLabel>
                  <Input type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} placeholder={smtp.has_password ? "••••••••" : "App password"} className="font-mono" data-testid="smtp-password" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>From Name</FieldLabel>
                  <Input value={smtp.from_name} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} placeholder="Digital Yantra" data-testid="smtp-from-name" />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>From Email</FieldLabel>
                  <Input value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} placeholder="billing@yourstore.com" className="font-mono" data-testid="smtp-from-email" />
                </div>
              </div>
              <p className="text-[11px] text-[#64748B]">Gmail: enable 2FA and use an App Password (not your login password). Port 587 = STARTTLS, Port 465 = SSL.</p>
              <SaveBtn onClick={saveSmtp} saving={savingSmtp} label="Save SMTP profile" testId="save-smtp" />
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

const emptyUser = { name: "", email: "", password: "", role: "staff" };

function UsersPanel({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try { const { data } = await api.get("/users"); setUsers(data || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchUsers(); }, []);

  const openNew = () => { setEditId(null); setForm(emptyUser); setOpen(true); };
  const openEdit = (u) => { setEditId(u.id); setForm({ name: u.name, email: u.email, password: "", role: u.role }); setOpen(true); };

  const save = async () => {
    if (!form.email.trim() || !form.name.trim()) { toast.error("Name and email are required"); return; }
    if (!editId && form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      if (editId) {
        const payload = { name: form.name, email: form.email, role: form.role };
        if (form.password) payload.password = form.password;
        const { data } = await api.put(`/users/${editId}`, payload);
        setUsers((p) => p.map((x) => (x.id === editId ? data : x)));
        toast.success("User updated");
      } else {
        const { data } = await api.post("/users", { name: form.name, email: form.email, password: form.password, role: form.role });
        setUsers((p) => [...p, data]);
        toast.success("User created");
      }
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    const id = deleteId; const prev = users;
    setUsers((p) => p.filter((x) => x.id !== id));
    setDeleteId(null);
    try { await api.delete(`/users/${id}`); toast.success("User deleted"); }
    catch (e) { setUsers(prev); toast.error(formatApiError(e)); }
  };

  return (
    <Section>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-[#0055FF]" />
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B]">Users & Roles</div>
            <h2 className="text-xl font-display font-medium tracking-tight">Admin & staff logins</h2>
          </div>
        </div>
        <Button onClick={openNew} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2" data-testid="add-user-btn">
          <Plus className="w-4 h-4" /> New user
        </Button>
      </div>
      <p className="text-[13px] text-[#475569]">Staff can run all operations (sales, invoices, service, attendance) but can't see financial totals, expenses, payroll/base-salary, past-month invoices, or these settings.</p>

      <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader className="bg-[#F8F9FA]">
            <TableRow>
              <TableHead className="text-[11px] font-mono uppercase tracking-wider">Name</TableHead>
              <TableHead className="text-[11px] font-mono uppercase tracking-wider">Email</TableHead>
              <TableHead className="text-[11px] font-mono uppercase tracking-wider">Role</TableHead>
              <TableHead className="text-[11px] font-mono uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-sm text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</TableCell></TableRow>
            ) : users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                <TableCell className="text-sm font-medium">{u.name}</TableCell>
                <TableCell className="font-mono text-xs">{u.email}</TableCell>
                <TableCell>
                  <span className={cn("text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded inline-flex items-center gap-1",
                    u.role === "admin" ? "bg-[#EEF2FA] text-[#0055FF]" : "bg-slate-100 text-[#475569]")}>
                    {u.role === "admin" && <Shield className="w-3 h-3" />}{u.role}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}><Pencil className="w-4 h-4 text-[#0055FF]" /></Button>
                    {u.id !== currentUserId && (
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(u.id)} data-testid={`delete-user-${u.email}`}><Trash2 className="w-4 h-4 text-[#EF4444]" /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editId ? "Edit user" : "New user"}</DialogTitle>
            <DialogDescription>{editId ? "Update this login's details." : "Create a new admin or staff login."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="font-mono" data-testid="user-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">{editId ? "New Password (optional)" : "Password"}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editId ? "Leave blank to keep" : "Min 6 characters"} data-testid="user-password" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">Role</Label>
                <div className="inline-flex rounded-md border border-[#E2E8F0] overflow-hidden w-full">
                  {["staff", "admin"].map((r) => (
                    <button key={r} type="button" onClick={() => setForm({ ...form, role: r })} data-testid={`user-role-${r}`}
                      className={cn("flex-1 h-9 text-sm capitalize transition-colors", form.role === r ? "bg-[#0055FF] text-white font-medium" : "bg-white text-[#475569] hover:bg-slate-50")}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0055FF] hover:bg-[#0040C1] text-white" data-testid="save-user-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? "Save changes" : "Create user")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>They will no longer be able to log in. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-delete-user">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  );
}
